<!-- eslint-disable max-len -->
<template lang="pug">
web-app-layout(:showLogo="true")
  .min-h-screen.bg-white.pb-24
    //- Event/Sticker Banner with Overlay
    .pos-r
      //- Main Image
      template(v-if="item.url")
        img(:src="item.url").w-100.banner-image

      //- Date/Location/Virtual Overlay
      .banner-overlay
        //- Collectible Sticker Callout (floating)
        .sticker-callout(v-if="showStickerCallout")
          img(:src="item.url" v-if="item.url").sticker-callout-image
          .sticker-callout-text Collect Sticker with RSVP

        .d-flex.align-items-center.gap-4.text-white
          .flex-1.d-flex.align-items-center.gap-2
            span.banner-text(v-if="item.eventDate") {{ getformattedDate(item.eventDate) }}
            span.banner-text(v-if="item.eventDate") • {{ getformattedTime(item.eventDate) }}
          .flex-1(v-if="item.location")
            span.banner-text.truncate {{ item.location }}

        //- Virtual Badge
        .virtual-badge(v-if="item.isVirtual") VIRTUAL

    //- Main Content
    .px-4.pt-4
      //- Badge and Actions
      .d-flex.align-items-center.justify-content-between.mb-4
        .badge.bg-primary.text-white.sticker-badge(v-if="item.campaignType") {{ item.campaignType }}
        .d-flex.align-items-center.gap-2
          button.action-btn(@click="toggleFavorite")
            img(src="@/assets/icons/bell.svg" alt="Favorite")
          button.action-btn(@click="shareSticker")
            img(src="@/assets/icons/message.svg" alt="Share")

      //- Creator
      .d-flex.align-items-center.gap-2.mb-3.creator-row(
        :class="{ 'is-clickable': creatorProfilePath }"
        @click="handleCreatorClick"
      )
        template(v-if="item.organization != null")
          img(:src="item.organization.avatar" v-if="item.organization.avatar").creator-avatar
          img(src="@/assets/images/default-profile.jpeg" v-else).creator-avatar
          span.creator-name {{ item.organization.name }}
        template(v-else)
          img(:src="item.user.avatar" v-if="item.user.avatar").creator-avatar
          img(src="@/assets/images/default-profile.jpeg" v-else).creator-avatar
          span.creator-name {{ item.user.firstname }} {{ item.user.lastname }}

      //- Title and Price
      h2.event-title.mb-2 {{ item.title }}
      .event-price.mb-4(v-if="item.cost !== null")
        span(v-if="item.cost == 0") Free
        .price-with-fees(v-else)
          .price-amount {{ formatPriceAmountWithFees(item.cost) }}
          .price-label incl. fees

      //- Ticket Tiers Section (for Ticket type)
      .ticket-tiers-section.mb-6(v-if="item.campaignType === 'Ticket'")
        h3.section-title.mb-3 Select Tickets

        //- Main Ticket Tier
        .ticket-tier
          .d-flex.align-items-start.justify-content-between.gap-4.mb-3
            div.flex-1
              h4.tier-name.mb-1 {{ item.title }}
              .tier-price
                span(v-if="item.cost == 0") Free
                .price-with-fees(v-else)
                  .price-amount {{ formatPriceAmountWithFees(item.cost) }}
                  .price-label incl. fees

            //- Quantity Controls
            .quantity-controls.d-flex.align-items-center.gap-2
              button.qty-btn.minus-btn(@click="decreaseQuantity" :disabled="quantity === 0")
                img(src="@/assets/icons/chevron-down.svg" alt="Decrease")
              span.qty-display {{ quantity }}
              button.qty-btn.plus-btn(@click="increaseQuantity" :disabled="quantity >= 10")
                img(src="@/assets/icons/plus.svg" alt="Increase")

          p.tier-description(v-if="item.description") {{ item.description }}

        //- Glow Tier (if exists)
        .ticket-tier.glow-tier(v-if="item.glow")
          .glow-badge-modern
            .d-flex.align-items-start.justify-content-between.gap-4.mb-3
              div.flex-1
                h4.tier-name.mb-1 {{ item.glow.title || 'GLOW EVENT' }}
                p.tier-price Free
              button.register-btn(v-if="!isGlowRegistered" @click="registerGlow") Register
              button.register-btn.registered(v-else disabled) Registered

            p.glow-description(v-if="item.glow.description") {{ item.glow.description }}
            .glow-date(v-if="item.glow.glowStartDate && item.glow.glowEndDate")
              | {{ getformattedDate(item.glow.glowStartDate) }} - {{ getformattedDate(item.glow.glowEndDate) }}

      //- Details Section (moved AFTER tiers)
      .details-section.mb-6
        h3.section-title.mb-2 Details
        p.details-text {{ item.description }}

        .location-info.mt-3(v-if="item.location")
          h6.location-label Location
          p.location-text {{ item.location }}

      //- Gallery Section
      .gallery-section.mb-6(v-if="item.gallery && item.gallery.length > 0")
        h3.section-title.mb-3 Gallery
        .gallery-grid
          template(v-for="(galleryItem, index) in item.gallery" :key="index")
            .gallery-item.gallery-item--clickable(
              v-if="galleryItem.type === 'video'"
              @click="openLightbox(galleryItem)"
            )
              video.gallery-video(:src="galleryItem.url")
              .gallery-item__zoom-hint
                img(src="@/assets/icons/magnify.svg" alt="View")
            .gallery-item.gallery-item--clickable(v-else @click="openLightbox(galleryItem)")
              img.gallery-image(:src="galleryItem.url" :alt="`Gallery image ${index + 1}`")
              .gallery-item__zoom-hint
                img(src="@/assets/icons/magnify.svg" alt="View")

      //- Lightbox
      transition(name="c-lightbox-fade")
        .c-lightbox(v-if="lightboxItem" @click.self="closeLightbox")
          .c-lightbox__toolbar
            button.c-lightbox__zoom-btn(type="button" @click="zoomOut" :disabled="lightboxZoom <= 0.5") −
            span.c-lightbox__zoom-label {{ Math.round(lightboxZoom * 100) }}%
            button.c-lightbox__zoom-btn(type="button" @click="zoomIn" :disabled="lightboxZoom >= 4") +
            button.c-lightbox__close(type="button" @click="closeLightbox") ×
          .c-lightbox__stage(@click.self="closeLightbox")
            video.c-lightbox__media(
              v-if="lightboxItem.type === 'video'"
              :src="lightboxItem.url"
              controls
              :style="{ transform: 'scale(' + lightboxZoom + ')' }"
            )
            img.c-lightbox__media(
              v-else
              :src="lightboxItem.url"
              alt="Full size preview"
              :style="{ transform: 'scale(' + lightboxZoom + ')' }"
            )

      //- FAQ Section
      .faq-section.mb-6
        .d-flex.align-items-center.justify-content-between.mb-3
          h3.section-title FAQ

        .faq-list
          .faq-item(v-for="(faq, index) in faqs" :key="index")
            button.faq-question(@click="toggleFAQ(index)").w-100.d-flex.align-items-center.justify-content-between
              span {{ faq.question }}
              img.chevron-icon(:class="{ 'rotated': expandedFaqIndex === index }" src="@/assets/icons/chevron-down.svg" alt="Toggle")
            .faq-answer(v-if="expandedFaqIndex === index") {{ faq.answer }}

        //- Contact Form
        .contact-form(v-if="showContactForm")
          .contact-header
            img(src="@/assets/icons/message.svg" alt="Message").me-2
            div.flex-1
              p.contact-title Message {{ getCreatorName() }}
              p.contact-subtitle Get answers about {{ item.title }}
          textarea.contact-textarea(
            placeholder="Type your question here..."
            rows="3"
            v-model="contactMessage"
          )
          .contact-actions.d-flex.gap-2
            button.btn-cancel(@click="showContactForm = false") Cancel
            button.btn-send(@click="sendMessage") Send Message

      //- Waiver Section
      .waiver-section.mb-6(v-if="item.waiverURL && item.waiverURL.indexOf('http')>=0")
        h3.section-title.mb-2 Waiver
        iframe.waiver-frame(
          :src="item.waiverURL"
          :title="item.waiverURL"
        )

    //- Fixed Bottom Bar
    .fixed-bottom-bar
      .d-flex.align-items-center.gap-3.w-100
        .flex-1(v-if="item.campaignType === 'Ticket'")
          p.cart-summary(v-if="quantity > 0") {{ quantity }} ticket{{ quantity > 1 ? 's' : '' }}
          p.cart-summary.text-muted(v-else) Select tickets to continue
          p.cart-total(v-if="quantity > 0") ${{ (item.cost * quantity).toFixed(2) }}

        el-button.action-button(
          type='success'
          round
          @click='onStickerActionButtonClick'
          v-loading='loading'
          :disabled="item.campaignType === 'Ticket' && quantity === 0"
        )
          span(v-if="item.cost == 0 || isStickerPurchased === true") Add Sticker To Your Profile
          span(v-else-if="item.campaignType === 'Ticket'") Add To Cart
          span(v-else-if="item.campaignType === 'Campaign'") Donate
          span(v-else) Purchase

        button.calendar-btn(
          v-if="item.campaignType === 'Ticket' && item.eventDate"
          @click='addToCalendarClick(item.eventDate, item.description, item.location, item.title)'
        )
          fai(type="fal" icon="calendar-plus")
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import gqlRequest from '../dashboard/gql-request';
import { makeRequest } from '@/graphs/index';
import { trackStickerShare } from '@/graphs/gql';
import { addToCalendarClick } from '@/utils/calendarUtils.ts';
import { formatPriceWithFees, formatPriceAmountWithFees } from '@/utils/priceWithFees';
import { getCreatorProfilePath } from '@/utils/profileNavigation';
import { htmlToShareText } from '@/utils/stickerShareUrl';

dayjs.extend(utc);

const defaultFAQs = [
  {
    question: "What's the refund policy?",
    answer: 'Tickets can be refunded up to 48 hours before the event. After that, tickets are non-refundable but transferable to another person.',
  },
  {
    question: 'Can I transfer my ticket to someone else?',
    answer: "Yes! You can transfer your ticket to anyone by going to your orders page and selecting the 'Transfer' option next to the ticket.",
  },
  {
    question: 'Will I receive a physical ticket?',
    answer: 'All tickets are digital and will be sent to your email and available in your wallet within the app. Simply show the QR code at the event entrance.',
  },
  {
    question: 'What should I bring to the event?',
    answer: 'Please bring a valid ID and your digital ticket (available in the app or via email). Specific requirements may vary by event.',
  },
  {
    question: 'Is there parking available?',
    answer: 'Please check the event details for specific parking information. Many venues offer on-site or nearby parking options.',
  },
];

export default defineComponent({
  name: 'WebAppSticker',
  components: { WebAppLayout },
  setup() {
    const {
      route, router, getPublicSticker,
    } = gqlRequest();

    const { stickerId } = route.params;
    const firstCauseName = ref('');
    const store = useStore();
    const isPromptDownloadApp = ref(false);
    const isStickerPurchased = ref(false);
    const loading = ref(false);

    const quantity = ref(0);
    const isFavorited = ref(false);
    const isGlowRegistered = ref(false);
    const expandedFaqIndex = ref<number | null>(null);
    const showContactForm = ref(false);
    const contactMessage = ref('');
    const showStickerCallout = computed(() => item.campaignType === 'Ticket' && item.url);
    const faqs = ref(defaultFAQs);

    const item = reactive({
      id: null,
      title: '',
      description: '',
      url: '',
      published: false,
      tags: ref([]),
      cost: 0,
      campaignType: null,
      causes: ref(['']),
      maxQuantity: 0,
      maxDonation: null,
      donationMultiplier: 1,
      impact: null,
      startDate: null,
      endDate: null,
      eventDate: null,
      location: '',
      isVirtual: false,
      user: { firstname: '', lastname: '', avatar: null },
      organization: { name: '', avatar: null },
      creatorTitle: null,
      isFeatured: false,
      isCausesSearchAllowed: false,
      glow: null,
      gallery: ref([]),
      waiverURL: '',
    });

    function checkAuth() {
      if (store.getters['users/isMeSignedIn']) {
        if (route.path !== `/web/sticker/${stickerId}`) {
          router.push(`/web/sticker/${stickerId}`);
        }
      }
    }

    function getCausesList() {
      let firstCause = '';
      console.log('causes are ', item.causes);
      if (item.causes.length) {
        item.causes.forEach((x, index) => {
          if (index === 0) {
            firstCause = `${firstCause} ${x.split(' : ')[0]}`;
          } else { firstCause = `${firstCause}, ${x.split(' : ')[0]}`; }
        });
        console.log('firstCause', firstCause);
      }
      return firstCause;
    }

    async function getItem() {
      const sticker = await getPublicSticker(stickerId);
      Object.assign(item, sticker);
      item.causes = (sticker.causes !== null && sticker.causes !== '') ? sticker.causes.split(' |=| ') : [];
      getCausesList();
    }

    function getformattedDate(timeOffset: string | number | Date) {
      const t = dayjs(timeOffset);
      return `${t.format('MMM DD, YY')}`;
    }

    function getformattedTime(timeOffset: string | number | Date) {
      const t = dayjs(timeOffset);
      return `${t.format('hh:mm A')}`;
    }

    function increaseQuantity() {
      if (quantity.value < 10) {
        quantity.value += 1;
      }
    }

    function decreaseQuantity() {
      if (quantity.value > 0) {
        quantity.value -= 1;
      }
    }

    function toggleFavorite() {
      isFavorited.value = !isFavorited.value;
    }

    async function trackStickerShareComplete() {
      if (!item.id) return;

      try {
        await makeRequest(
          'mutation',
          trackStickerShare,
          { stickerId: item.id.toString() },
          {},
        );
      } catch (err) {
        console.error('Failed to track sticker share:', err);
      }
    }

    async function shareSticker() {
      if (navigator.share) {
        try {
          await navigator.share({
            title: item.title,
            text: htmlToShareText(item.description),
            url: window.location.href,
          });
          await trackStickerShareComplete();
        } catch (error: any) {
          if (error?.name !== 'AbortError') {
            console.log('Error sharing', error);
          }
        }
      }
    }

    function registerGlow() {
      const glowId = item.glow?.id;
      const params = new URLSearchParams({ stickerId: String(stickerId) });
      if (glowId) params.set('glowId', glowId);
      router.push(`/auth/web/signup?${params.toString()}`);
    }

    function toggleFAQ(index: number) {
      expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
    }

    function getCreatorName() {
      if (item.organization != null) {
        return item.organization.name;
      }
      return `${item.user.firstname} ${item.user.lastname}`;
    }

    function sendMessage() {
      console.log('Sending message:', contactMessage.value);
      showContactForm.value = false;
      contactMessage.value = '';
    }

    async function onStickerActionButtonClick() {
      loading.value = true;
      try {
        router.push(`/auth/web/signup?stickerId=${stickerId}`);
      } catch (err) {
        console.error('Error navigating to signup:', err);
        loading.value = false;
      }
    }

    onMounted(async () => {
      loading.value = true;
      await checkAuth();
      await getItem();
      store.commit('shared/SET_MAIN_MENU_COLLAPSE', false);
      loading.value = false;
    });

    const lightboxItem = ref<{ type: string; url: string } | null>(null);
    const lightboxZoom = ref(1);
    const ZOOM_STEP = 0.25;

    const openLightbox = (media: { type: string; url: string }) => {
      lightboxItem.value = media;
      lightboxZoom.value = 1;
    };
    const closeLightbox = () => { lightboxItem.value = null; lightboxZoom.value = 1; };
    const zoomIn = () => {
      lightboxZoom.value = Math.min(4, +(lightboxZoom.value + ZOOM_STEP).toFixed(2));
    };
    const zoomOut = () => {
      lightboxZoom.value = Math.max(0.5, +(lightboxZoom.value - ZOOM_STEP).toFixed(2));
    };
    const onLightboxBackdrop = (e: MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains('c-lightbox')) closeLightbox();
    };

    const creatorProfilePath = computed(() => getCreatorProfilePath(
      item.entity,
      item.organization,
      item.user,
    ));

    const handleCreatorClick = () => {
      if (creatorProfilePath.value) {
        router.push(creatorProfilePath.value);
      }
    };

    return {
      route,
      router,
      loading,
      item,
      quantity,
      isFavorited,
      isGlowRegistered,
      expandedFaqIndex,
      showContactForm,
      contactMessage,
      showStickerCallout,
      faqs,
      getCausesList,
      firstCauseName,
      onStickerActionButtonClick,
      isPromptDownloadApp,
      isStickerPurchased,
      getformattedDate,
      getformattedTime,
      addToCalendarClick,
      increaseQuantity,
      decreaseQuantity,
      toggleFavorite,
      shareSticker,
      registerGlow,
      toggleFAQ,
      getCreatorName,
      creatorProfilePath,
      handleCreatorClick,
      sendMessage,
      lightboxItem,
      lightboxZoom,
      formatPriceWithFees,
      formatPriceAmountWithFees,
      openLightbox,
      closeLightbox,
      zoomIn,
      zoomOut,
      onLightboxBackdrop,
    };
  },
});
</script>
<style lang="scss" scoped>
// Banner Styles
.banner-image {
  height: 16rem;
  object-fit: cover;
}

.banner-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  padding: 0.625rem 1rem;
}

.banner-text {
  font-size: 0.75rem;
  font-weight: 500;
}

.virtual-badge {
  background: rgba(255, 255, 255, 0.9);
  color: #1a1a1a;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  margin-top: 0.5rem;
  display: inline-block;
}

// Sticker Callout
.sticker-callout {
  position: absolute;
  top: -4rem;
  left: 1rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 9999px;
  padding: 0.25rem 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  animation: float-sticker 3s ease-in-out infinite;
}

.sticker-callout-image {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
  transform: scale(3.2);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  mix-blend-mode: normal;
}

.sticker-callout-text {
  font-size: 0.625rem;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  padding-right: 0.5rem;
  margin-left: 1.25rem;
}

@keyframes float-sticker {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

// Badge Styles
.sticker-badge {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
}

// Action Buttons
.action-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  img {
    width: 1rem;
    height: 1rem;
    opacity: 0.8;
  }

  &:hover {
    background: #e5e7eb;
  }
}

// Creator
.creator-row {
  &.is-clickable {
    cursor: pointer;

    &:hover {
      .creator-name {
        color: #0d9488;
      }
    }
  }
}

.creator-avatar {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  object-fit: cover;
}

.creator-name {
  font-size: 0.875rem;
  color: #6b7280;
  text-decoration: underline;
}

// Title & Price
.event-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.event-price {
  font-size: 1.25rem;
  color: #225c6b;
  font-weight: 600;
  margin-bottom: 1.5rem;

  .price-with-fees {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .price-amount {
      font-size: 1.25rem;
      color: #225c6b;
      font-weight: 600;
      line-height: 1.2;
    }

    .price-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
      text-transform: lowercase;
    }
  }
}

// Section Titles
.section-title {
  font-size: 1.125rem;
  font-weight: 700;
}

// Ticket Tiers
.ticket-tier {
  border: 2px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(13, 148, 136, 0.3);
  }
}

.glow-tier {
  border-color: rgba(255, 219, 15, 0.6);
  background: #fff;
}

.tier-name {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.tier-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #225c6b;

  .price-with-fees {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .price-amount {
      font-size: 1.5rem;
      font-weight: 700;
      color: #225c6b;
      line-height: 1.2;
    }

    .price-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
      text-transform: lowercase;
    }
  }
}

.tier-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.75rem;
}

// Quantity Controls
.quantity-controls {
  flex-shrink: 0;
}

.qty-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  img {
    width: 1rem;
    height: 1rem;
  }

  &.minus-btn {
    background: #f3f4f6;

    &:hover:not(:disabled) {
      background: #e5e7eb;
    }
  }

  &.plus-btn {
    background: #0d9488;

    img {
      filter: brightness(0) invert(1);
    }

    &:hover:not(:disabled) {
      background: rgba(13, 148, 136, 0.9);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
}

.qty-display {
  font-size: 1.125rem;
  font-weight: 600;
  width: 2rem;
  text-align: center;
}

// Glow Badge Modern
.glow-badge-modern {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #1a1a1a;
  padding: 1rem;
  border-radius: 0.75rem;
  box-shadow: 0 10px 40px rgba(251, 191, 36, 0.4);
  animation: glow-pulse 2s ease-in-out infinite;
}

.register-btn {
  background: linear-gradient(to right, #fbbf24, #f59e0b);
  color: #1a1a1a;
  border: none;
  border-radius: 9999px;
  padding: 0.5rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &.registered {
    background: #e5e7eb;
    color: #6b7280;
    cursor: default;
  }
}

.glow-description {
  font-size: 0.875rem;
  color: #1a1a1a;
  margin-top: 0.75rem;
}

.glow-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-top: 0.5rem;
}

@keyframes glow-pulse {
  0%, 100% {
    box-shadow: 0 10px 40px rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 10px 60px rgba(251, 191, 36, 0.6);
  }
}

// Details Section
.details-text {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.625;
}

.location-label {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.location-text {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

// Gallery
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.gallery-item {
  position: relative;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #f3f4f6;

  &--clickable {
    cursor: pointer;

    img.gallery-image,
    video.gallery-video {
      transition: transform 0.2s;
    }

    &:hover img.gallery-image,
    &:hover video.gallery-video {
      transform: scale(1.04);
    }

    &:hover .gallery-item__zoom-hint {
      opacity: 1;
    }
  }

  &__zoom-hint {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;

    img {
      width: 28px;
      height: 28px;
      filter: brightness(0) invert(1);
      object-fit: contain;
    }
  }
}

.gallery-image,
.gallery-video {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
}

.gallery-video {
  border: none;
}

// Lightbox
.c-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    flex-shrink: 0;
  }

  &__zoom-btn {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.25);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  &__zoom-label {
    color: #ddd;
    font-size: 13px;
    min-width: 46px;
    text-align: center;
  }

  &__close {
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 22px;
    line-height: 1;
    margin-left: 8px;
    transition: background 0.15s;

    &:hover {
      background: rgba(220, 50, 50, 0.6);
    }
  }

  &__stage {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    padding: 16px;
  }

  &__media {
    max-width: 90vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 6px;
    transform-origin: center center;
    transition: transform 0.2s ease;
    display: block;
  }
}

.c-lightbox-fade-enter-active,
.c-lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.c-lightbox-fade-enter-from,
.c-lightbox-fade-leave-to {
  opacity: 0;
}

// FAQ Section
.message-host-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background: rgba(13, 148, 136, 0.1);
  color: #0d9488;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
  font-weight: 600;

  img {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:hover {
    background: rgba(13, 148, 136, 0.2);
  }
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.faq-item {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  background: white;
}

.faq-question {
  padding: 1rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.875rem;
  font-weight: 600;

  &:hover {
    background: #f9fafb;
  }

  .chevron-icon {
    width: 1.25rem;
    height: 1.25rem;
    color: #6b7280;
    flex-shrink: 0;
    transition: transform 0.2s;

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

.faq-answer {
  padding: 0 1rem 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.625;
}

// Contact Form
.contact-form {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(13, 148, 136, 0.05);
  border: 1px solid rgba(13, 148, 136, 0.2);
  border-radius: 0.75rem;
}

.contact-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  img {
    width: 1.25rem;
    height: 1.25rem;
    color: #0d9488;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
}

.contact-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.contact-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
}

.contact-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  resize: none;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #0d9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
}

.contact-actions {
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-send {
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-cancel {
  background: white;
  color: #6b7280;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
}

.btn-send {
  background: #0d9488;
  color: white;

  &:hover {
    background: rgba(13, 148, 136, 0.9);
  }
}

// Waiver Section
.waiver-frame {
  width: 100%;
  height: 250px;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

// Fixed Bottom Bar
.fixed-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 1rem;
  z-index: 40;
}

.cart-summary {
  font-size: 0.75rem;
  color: #6b7280;

  &.text-muted {
    font-size: 0.875rem;
  }
}

.cart-total {
  font-size: 1.25rem;
  font-weight: 700;
}

.action-button {
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 2rem;
  height: 3rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.calendar-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 9999px;
  background: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
  }
}

// Utility Classes
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flex-1 {
  flex: 1;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}

.gap-4 {
  gap: 1rem;
}
</style>
